import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import { findArticle } from './articleLanguages';
import { isArticleLanguage } from '../i18n/articleLanguages';
import { translations } from '../i18n/config';
import manifest from '../i18n/articleManifest.json';
import classes from './localizedArticle.module.scss';
import ExplainerWrapper from './explainerWrapper';
import SpecificGameContainer from '../containers/specificGame';
import articleClasses from '../containers/introduction/introduction.module.scss';

type ContentNode = string | { tag: string; href?: string; children: ContentNode[] };
interface Article { path: string; title: string; description: string; keywords?: string; gameKey?: string; body: ContentNode[] }
const renderContent = (nodes: ContentNode[], language: string): React.ReactNode => nodes.map((node, index) => {
  if (typeof node === 'string') return node;
  let href = node.href;
  if (href?.startsWith('/') && manifest.some(entry => entry.path === href?.toLowerCase())) href = '/' + language + href.toLowerCase();
  else if (href?.startsWith('/') && !href.startsWith('//')) {
    const destination = new URL(href, window.location.origin);
    if (!destination.searchParams.has('language')) destination.searchParams.set('language', language);
    href = destination.pathname + destination.search + destination.hash;
  }
  return React.createElement(node.tag, { key: index, ...(href ? { href } : {}) }, node.tag === 'br' ? undefined : renderContent(node.children, language));
});
export default function LocalizedArticle({ handle }: { handle?: any }) {
  const { articleLanguage = '', articleSlug = '' } = useParams();
  const language = isArticleLanguage(articleLanguage) ? articleLanguage : undefined;
  const { article: registered } = findArticle('/' + articleLanguage + '/' + articleSlug);
  const [article, setArticle] = useState<Article>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setArticle(undefined); setFailed(false);
    if (!language || !registered) return;
    const controller = new AbortController();
    fetch('/article-content/' + language + '.json', { signal: controller.signal })
      .then(response => { if (!response.ok) throw Error('Article unavailable'); return response.json(); })
      .then((entries: Article[]) => { const found = entries.find(entry => entry.path === registered.path); if (!found) throw Error('Article missing'); setArticle(found); })
      .catch(error => { if (error.name !== 'AbortError') setFailed(true); });
    return () => controller.abort();
  }, [language, registered]);
  if (!language || !registered) return <main className={classes.page}><Helmet><meta name="robots" content="noindex" /></Helmet><h1>404</h1><a href="/">PlayCloud</a></main>;
  const ui = translations[language];
  if (!article) return <div className={classes.page}><p role={failed ? 'alert' : 'status'}>{failed ? ui.gameShowcase.loadError : ui.gameShowcase.loading}</p></div>;
  const body = <article lang={language} dir={['he', 'ar'].includes(language) ? 'rtl' : 'ltr'}>{renderContent(article.body, language)}</article>;
  if (article.gameKey) return <SpecificGameContainer key={language + article.path} handle={handle}
    predeterminedGameKey={article.gameKey} title={article.title} description={article.description}
    keywords={article.keywords} introduction={body} />;
  return <>
    <Helmet><title>{article.title}</title><meta name="description" content={article.description} />{article.keywords && <meta name="keywords" content={article.keywords} />}</Helmet>
    <ExplainerWrapper title={article.title}><div className={articleClasses.wrapper}>{body}</div></ExplainerWrapper>
  </>;
}
